import { Link } from "react-router";
import { RouteFactory } from "@app/routePaths";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import { AdUnitNames } from "@components/Advertisement/utils/types";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import GAData from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import { useBreadCrumbState } from "@components/Header/helpers";
import {
  renderCustomOgImageObject,
  renderMetaDescription,
  renderPageTitleV2,
} from "@components/MetaTags/helpers";
import MetaTags from "@components/MetaTags/MetaTags";
import ListingPageNewsletter from "@features/newsletter/ListingPageNewsletter";
import { useGetPosthogVariant } from "@features/posthog/hooks/useGetPosthogVariant";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { useMostReadArticleList } from "@hooks/useMostReadArticleList";
import { useTrimmedBreakingNews } from "@hooks/useTrimmedBreakingNews";
import { useWindowSize } from "@hooks/useWindowSize";
import ThriveNewsletter from "@pages/Article/components/ArticleNewsletter/ThriveNewsletter";
import BreakingNews from "@pages/Section/components/BreakingNews/BreakingNews";
import MostPopular from "@pages/Section/components/MostPopular/MostPopular";
import PrPageIntroduction from "@pages/Section/components/PrPageIntroduction";
import SectionHeading from "@pages/Section/components/SectionHeading";
import SectionSubhHeading from "@pages/Section/components/SectionSubhHeading";
import {
  getSectionHeadingClass,
  getSectionHeadingText,
} from "@pages/Section/helpers";
import { SectionContext } from "@pages/Section/utils/types";
import Listing from "@particles/listing/Listing";
import { sectionNavigationItems } from "@util/constant";
import { cn, getSectionPageJSONLD, toTitleCase } from "@util/helpers";

type DefaultSectionLayoutProps = Pick<SectionContext, "data">;

/**
 * Default layout for section pages.
 * https://www.figma.com/design/rmzJoZwUpb3iLm6lMDXJ2W/BT-MASTER--WEB-?node-id=1209-14339&t=3orrZEa3A87RLS71-4
 */
const DefaultSectionLayout = ({
  data: { entity, title, overview, seoFooterData, relativePath },
}: DefaultSectionLayoutProps): React.ReactElement => {
  const { isScreenLG } = useWindowSize();
  const mostPopular = useMostReadArticleList();
  const breakingNews = useTrimmedBreakingNews();

  const { name, urlPath, uniqueName, parent } = entity;

  const { breadcrumbsRef, isShowBreadCrumb } = useBreadCrumbState();
  const adNames: AdUnitNames[] =
    relativePath === RouteFactory.paidPressRelease
      ? ["lb1", "imu2", "bn1", "catfish"]
      : ["lb1", "imu2", "bn1", "prestitial", "catfish"];

  const mainCategory = relativePath.substring(1);
  const parentKey = parent?.relativePath?.substring(1) || mainCategory;
  const parentCategoryObj = sectionNavigationItems[parentKey];

  const isEnabledFeatureFlagVariant =
    relativePath === RouteFactory.section("companies-markets");
  const { gaVariant } = useGetPosthogVariant({
    flag: "feature_dummy_experiment",
    isEnabledFeatureFlagVariant,
  });

  const gaTitle = urlPath.endsWith("/latest") ? `${name} Latest` : name;
  const ogImageData = renderCustomOgImageObject({
    kind: "section",
    sectionName: name,
  });

  const sectionHeadingClass = getSectionHeadingClass(relativePath);
  const sectionHeadingText = getSectionHeadingText(relativePath, name);

  return (
    <div className="w-full" data-testid="default-section-layout">
      <GAData
        chapter1={name || ""}
        level2={parent?.name || ""}
        title={toTitleCase(gaTitle)}
        adblocker={typeof window !== undefined ? 0 : window.canRunAds}
        abVariant={gaVariant}
      />

      <AdSettings
        adNames={adNames}
        path={relativePath}
        adTargets={[{ key: "page", value: "listing" }]}
      />

      <MetaTags
        title={title}
        description={renderMetaDescription({
          kind: "section",
          sectionName: name,
        })}
        ogType="article"
        slug={relativePath}
        jsonLd={getSectionPageJSONLD(entity)}
        ogImageUrl={ogImageData.ogImageUrl}
        ogImageAlt={ogImageData.ogImageAlt}
        ogSiteName={renderPageTitleV2({
          kind: "section",
          sectionName: name,
        })}
      />

      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header
        displayBreadcrumb={isShowBreadCrumb}
        parentCategory={parentKey}
        childCategory={mainCategory}
        categoryDataMapping={sectionNavigationItems}
      />

      {relativePath !== RouteFactory.paidPressRelease ? <PulseEntry /> : null}

      <Container>
        <SectionHeading
          text={sectionHeadingText}
          className={sectionHeadingClass}
          hasBorder={!parentCategoryObj?.children?.length}
        />

        <>
          {!isScreenLG && relativePath.startsWith(RouteFactory.thrive) ? (
            <ThriveNewsletter
              className="pb-3"
              tagLine="Straight to your inbox."
              liner="Money, career and life hacks to help young adults stay ahead."
            />
          ) : null}

          {relativePath.startsWith(RouteFactory.newsletters) ? (
            <SectionSubhHeading
              to={`${RouteFactory.newsletterSignup}?ref=listing-menubar`}
              className="inline-block pb-3 font-poppins text-xs font-light text-gray-850 underline"
            >
              Select Your Newsletters
            </SectionSubhHeading>
          ) : null}
        </>

        <div ref={breadcrumbsRef}>
          <Breadcrumb
            className="m-auto"
            parentCategory={parentKey}
            childCategory={mainCategory}
            categoryDataMapping={sectionNavigationItems}
            extraBreadcrumbItems={
              <div
                className={cn(
                  "relative box-border flex shrink-0 items-center border-r border-gray-175 pr-3 font-poppins text-sm tracking-tightest text-gray-850 hover:opacity-80",
                  {
                    "font-bold": relativePath === parentCategoryObj?.link,
                  }
                )}
              >
                <Link
                  to={`${parentCategoryObj?.link}?ref=listing-menubar`}
                  reloadDocument
                >
                  Top stories
                </Link>
              </div>
            }
          />
        </div>

        <div className="pt-6">
          <>
            {relativePath === RouteFactory.paidPressRelease ? (
              <PrPageIntroduction />
            ) : null}
          </>

          <div className="grid lg:grid-cols-12">
            <div className="mb-6 lg:col-span-8">
              <Listing
                cardProps={overview}
                urlPath={urlPath}
                pageType={"section"}
                size={10}
                sections={[uniqueName]}
                isUseTemplateH
              />
            </div>

            <div className="mb-6 lg:col-span-4 lg:pl-6">
              {isScreenLG && relativePath.startsWith(RouteFactory.thrive) ? (
                <ThriveNewsletter
                  className="mx-auto mb-6 max-w-[300px]"
                  tagLine="Straight to your inbox."
                  liner="Money, career and life hacks to help young adults stay ahead."
                />
              ) : null}

              {isScreenLG ? (
                <ListingPageNewsletter className="mx-auto mb-6 max-w-[300px]" />
              ) : null}

              <div className="mb-6 lg:h-[800px]">
                <Ad
                  adType="imu2"
                  className="flex flex-col items-center justify-center lg:!sticky lg:top-18"
                />
              </div>

              <BreakingNews
                data={breakingNews}
                rootClassName="max-w-[300px] mx-auto mb-6"
              />

              <MostPopular
                data={mostPopular}
                rootClassName="max-w-[300px] mx-auto"
              />
            </div>
          </div>
        </div>
      </Container>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default DefaultSectionLayout;
